import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRouteApi } from '@tanstack/react-router';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useFormHook, useSetError } from '@Hooks/index';
import { useSaveOwner } from '@Hooks/api/useSaveOwner';
import { useNavigationManager } from '@Router/useNavigationManager';
import { RoutesMap } from '@Router/router.types';
import { routesModel } from '@Router/router.consts';
import { queryClient } from 'src/queryClient';
import { getAllowedCountries } from '@Hooks/api/fetchCountries.api';
import { OnboardingFormFields } from '@Types/types';
import { FieldValidationFieldName } from '@Store/slices/customer/customer.types';
import { getBusinessOwnerData } from '@Pages/happyFlowPages/ownersCenter/api/util';
import { UBOFormSchema, type UBOFormFields } from '../UBOFormScheme';
import {
  findCountryByCode,
  mapUBOToFormFields,
  prepareUBOFormValuesToServer,
} from '../addUBOUtils';
import type { ExtendedUseFormReturn } from '@Hooks/index';
import type { Country } from '@Types/country';
import type { Path } from 'react-hook-form';

const FIELD_VALIDATION_TO_FORM_FIELD: Record<
  FieldValidationFieldName,
  {
    field: Path<UBOFormFields>;
    errorKey: 'email.invalid' | 'phone' | 'ssn' | 'dateOfBirth' | 'required';
  } | null
> = {
  [FieldValidationFieldName.EMAIL]: {
    field: OnboardingFormFields.EMAIL,
    errorKey: 'email.invalid',
  },
  [FieldValidationFieldName.PHONE_NUMBER]: {
    field: OnboardingFormFields.PHONE_NUMBER,
    errorKey: 'phone',
  },
  [FieldValidationFieldName.SSN]: { field: OnboardingFormFields.SSN, errorKey: 'ssn' },
  [FieldValidationFieldName.BIRTH_DATE]: {
    field: OnboardingFormFields.DATE_OF_BIRTH,
    errorKey: 'dateOfBirth',
  },
  [FieldValidationFieldName.LINE1]: { field: OnboardingFormFields.LINE1, errorKey: 'required' },
  [FieldValidationFieldName.CITY]: { field: OnboardingFormFields.CITY, errorKey: 'required' },
  [FieldValidationFieldName.STATE]: { field: OnboardingFormFields.STATE, errorKey: 'required' },
  [FieldValidationFieldName.POSTAL_CODE]: { field: OnboardingFormFields.ZIP, errorKey: 'required' },
  [FieldValidationFieldName.BUSINESS_NAME]: null,
  [FieldValidationFieldName.BUSINESS_TYPE]: null,
  [FieldValidationFieldName.EIN]: null,
  [FieldValidationFieldName.NAICS]: null,
};

interface UseAddUBOLogicReturn {
  formMethods: ExtendedUseFormReturn<UBOFormFields>;
  handleSubmit: () => void;
  isLoading: boolean;
  isEditMode: boolean;
  onBackClick: () => void;
  countries: Country[];
}

const routeAPI = getRouteApi(routesModel[RoutesMap.ADD_OWNER].path);

export const useAddUBOLogic = (): UseAddUBOLogicReturn => {
  const { t } = useTranslation('enErrors');
  const { uboId } = routeAPI.useSearch();
  const { data: countriesData } = useSuspenseQuery(getAllowedCountries);
  const countries = countriesData.countries;
  const { getBusinessOwnerQueryData } = routeAPI.useRouteContext();
  const { data: existingUBO } = useSuspenseQuery(getBusinessOwnerQueryData);
  const { navigateToSpecificPage } = useNavigationManager();
  const { customerId, businessExternalId, isEmbedded } = getBusinessOwnerData();
  const { mutate: saveOwner, isPending: isSavingOwner } = useSaveOwner({
    customerId: customerId!,
    businessExternalId: businessExternalId!,
    isEmbedded,
  });
  // since the non-us scheme according to nationality and country, we need to watch both
  const [countryWatch, setCountryWatch] = useState<{ code: string; name: string } | undefined>(
    undefined
  );
  const [nationalityWatch, setNationalityWatch] = useState<string | undefined>(undefined);

  const isEditMode = !!uboId;
  const isUsResidentDefault = existingUBO ? !!existingUBO.ssn : true;

  const defaultValues = useMemo((): UBOFormFields => {
    return mapUBOToFormFields(existingUBO, isUsResidentDefault, countries);
  }, [existingUBO, isUsResidentDefault]);

  const formMethods: ExtendedUseFormReturn<UBOFormFields> = useFormHook({
    formSchema: UBOFormSchema(
      findCountryByCode(countryWatch?.code || '', countries),
      findCountryByCode(nationalityWatch || '', countries)
    ),
    formDefaultValues: defaultValues,
    schemaTitle: 'uboForm',
    values: defaultValues,
  });

  const reactiveNationality = formMethods.watch(OnboardingFormFields.NATIONALITY);
  const countryWatchForm = formMethods.watch(OnboardingFormFields.COUNTRY) as
    { code: string; name: string } | undefined;

  useEffect(() => {
    setCountryWatch(countryWatchForm);
  }, [countryWatchForm]);

  useEffect(() => {
    setNationalityWatch(reactiveNationality);
  }, [reactiveNationality]);

  useSetError({ formMethods });

  useEffect(() => {
    if (!existingUBO?.fieldValidationErrors?.length) return;

    for (const { fieldName } of existingUBO.fieldValidationErrors) {
      const mapping = FIELD_VALIDATION_TO_FORM_FIELD[fieldName];
      if (mapping) {
        formMethods.setError(mapping.field, { message: t(mapping.errorKey) });
      }
    }
  }, [existingUBO?.fieldValidationErrors]);

  const onBackClick = () => {
    navigateToSpecificPage(RoutesMap.OWNER_CENTER);
  };

  const handleSubmit = () => {
    const requestBody = prepareUBOFormValuesToServer(formMethods.getValues(), uboId);
    saveOwner(requestBody, {
      onSuccess: () => {
        navigateToSpecificPage(RoutesMap.OWNER_CENTER);
        queryClient.invalidateQueries({ queryKey: getBusinessOwnerQueryData.queryKey });
      },
    });
  };

  return {
    formMethods,
    handleSubmit,
    isLoading: isSavingOwner,
    isEditMode,
    onBackClick,
    countries,
  };
};
